import {
  ClockCounterIcon,
  CopyIcon,
  FileArchiveIcon,
  InfoIIcon,
  MoreVerticalIcon,
  PenIcon,
  PersonIcon,
  TagIcon,
} from '@moia-dev/pace-icons'
import {
  ActionItem,
  ActionItemDivider,
  ActionMenu,
  Icon,
  IconButton,
  LoadingIndicator,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useActionMenu,
} from '@moia-dev/pace-web'
import { memo, useEffect, useRef, useState } from 'react'
import type { RouteBuilderSavedRoutesQuery } from './SavedTrips.hook'

export type SavedTrip = RouteBuilderSavedRoutesQuery['savedRoutes'][number]

type SavedTripCardProps = {
  trip: SavedTrip
  selected?: boolean
  currentUserId?: string
  currentUserDisplayName?: string
  /** Map of userId → display name for non-current users */
  creatorNames?: Record<string, string>
  onSelect: (trip: SavedTrip) => void
  onEdit: (trip: SavedTrip) => void
  onDetails: (trip: SavedTrip) => void
  onArchive: (trip: SavedTrip) => void
  onDuplicate?: (trip: SavedTrip) => void
  onRename?: (trip: SavedTrip) => void
  onManageVtr?: (trip: SavedTrip) => void
  onMouseEnter?: (trip: SavedTrip) => void
  actionsDisabled?: boolean
  /** Hide the 3-dots action menu entirely (assign-trip modal flow). */
  hideActions?: boolean
}

const formatModifiedDate = (ms: number): string => {
  const d = new Date(ms)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = String(d.getFullYear()).slice(-2)
  return `${month}/${day}/${year}`
}

// Memoised: SavedTripsView stays mounted under the editor and re-renders on
// every shared-map update the editor makes, so the cards must not re-render
// with it. Every prop it passes is stable (useCallback handlers, Apollo trips).
export const SavedTripCard = memo(function SavedTripCard({
  trip,
  selected,
  currentUserId,
  currentUserDisplayName,
  creatorNames,
  onSelect,
  onEdit,
  onDetails,
  onArchive,
  onDuplicate,
  onRename,
  onManageVtr,
  onMouseEnter,
  actionsDisabled,
  hideActions,
}: SavedTripCardProps) {
  const creatorName = (() => {
    if (
      currentUserId &&
      trip.createdById === currentUserId &&
      currentUserDisplayName
    ) {
      return currentUserDisplayName
    }
    if (!trip.createdById || trip.createdById === 'system') return null
    return creatorNames?.[trip.createdById] ?? null
  })()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { getReferenceProps, ...menuProps } = useActionMenu({
    isOpen: isMenuOpen,
    setIsOpen: setIsMenuOpen,
  })

  // Close the menu once an in-flight archive/restore action completes.
  const prevActionsDisabledRef = useRef(actionsDisabled)
  useEffect(() => {
    if (prevActionsDisabledRef.current && !actionsDisabled) {
      setIsMenuOpen(false)
    }
    prevActionsDisabledRef.current = actionsDisabled
  }, [actionsDisabled])

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(trip)}
      onMouseEnter={() => onMouseEnter?.(trip)}
      onKeyDown={e => {
        if (e.key === 'Enter') onSelect(trip)
      }}
      css={theme => ({
        padding: `${theme.space.Space2} ${theme.space.Space4}`,
        borderRadius: theme.radius.RadiusMedium,
        border: `1px solid ${
          selected
            ? theme.semantic.ColorAccentDefault
            : theme.semantic.ColorAccessoryReduced
        }`,
        backgroundColor: selected
          ? theme.semantic.ColorSurfaceSelectedDefault
          : theme.semantic.ColorSurfaceDefault,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.space.Space2,
        // Selected wins over hover so hovering a selected row keeps its state.
        '&:hover': {
          backgroundColor: selected
            ? theme.semantic.ColorSurfaceSelectedDefault
            : theme.semantic.ColorSurfaceHovered,
        },
      })}
    >
      <div css={{ flex: 1, minWidth: 0 }}>
        {/* CSS-only truncation + tooltip (mirrors MoiaTruncatedTextTooltip): the
            `<Text>` is the direct TooltipTrigger child with `display: inline-block`
            + `maxWidth: 100%`, wrapped in a `min-width: 0; overflow: hidden` div so
            it clips. No scrollWidth measurement — the tooltip is always available
            and shows the full name on hover. */}
        <div css={{ minWidth: 0, overflow: 'hidden' }}>
          {/* delay=0: pace-web applies one number to both open and close (it's
              floating-ui's useHover `delay`), so the default 500 also lingers on
              hover-out. Zero removes the close delay. */}
          <Tooltip placement="top" delay={0}>
            <TooltipTrigger>
              <Text
                size="md"
                weight="bold"
                css={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {trip.name}
              </Text>
            </TooltipTrigger>
            <TooltipContent css={{ overflowWrap: 'anywhere' }}>
              {trip.name}
            </TooltipContent>
          </Tooltip>
        </div>
        <div
          css={theme => ({
            marginTop: 2,
            display: 'flex',
            alignItems: 'center',
            gap: theme.space.Space2,
            overflow: 'hidden',
            color: theme.semantic.ColorContentReduced,
          })}
        >
          <span
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              flexShrink: 0,
            }}
          >
            <Icon size={16}>
              <ClockCounterIcon />
            </Icon>
            <Text size="sm" color="reduced">
              {formatModifiedDate(trip.updatedMs)}
            </Text>
          </span>
          {creatorName && (
            <>
              <Text size="sm" color="reduced" css={{ flexShrink: 0 }}>
                {'|'}
              </Text>
              <span
                css={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  minWidth: 0,
                }}
              >
                <Icon size={16}>
                  <PersonIcon />
                </Icon>
                <Text
                  size="sm"
                  color="reduced"
                  css={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {creatorName}
                </Text>
              </span>
            </>
          )}
        </div>
      </div>

      {!hideActions && (
        <div
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <IconButton
            aria-label="Trip actions"
            size="compact"
            variant="plain"
            icon={<MoreVerticalIcon />}
            {...getReferenceProps()}
            ref={menuProps.refs.setReference}
          />
          <ActionMenu {...menuProps}>
            <ActionItem
              label="Details"
              leading={<InfoIIcon />}
              disabled={actionsDisabled}
              onClick={() => {
                setIsMenuOpen(false)
                onDetails(trip)
              }}
            />
            {onDuplicate && (
              <ActionItem
                label="Duplicate"
                leading={<CopyIcon />}
                disabled={actionsDisabled}
                onClick={() => {
                  setIsMenuOpen(false)
                  onDuplicate(trip)
                }}
              />
            )}
            {onRename && (
              <ActionItem
                label="Rename"
                leading={<PenIcon />}
                disabled={actionsDisabled}
                onClick={() => {
                  setIsMenuOpen(false)
                  onRename(trip)
                }}
              />
            )}
            <ActionItem
              label="Edit"
              leading={<PenIcon />}
              disabled={actionsDisabled}
              onClick={() => {
                setIsMenuOpen(false)
                onEdit(trip)
              }}
            />
            {onManageVtr && (
              <ActionItem
                label="VTR Selection"
                leading={<TagIcon />}
                disabled={actionsDisabled}
                onClick={() => {
                  setIsMenuOpen(false)
                  onManageVtr(trip)
                }}
              />
            )}
            <ActionItemDivider />
            <ActionItem
              label={trip.isArchived ? 'Restore' : 'Archive'}
              leading={
                actionsDisabled ? (
                  <LoadingIndicator size={16} />
                ) : (
                  <FileArchiveIcon />
                )
              }
              disabled={actionsDisabled}
              onClick={() => {
                onArchive(trip)
              }}
            />
          </ActionMenu>
        </div>
      )}
    </div>
  )
})
